import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import threeDotsIcon from '../../assets/three-dots-icon.svg'
import { RowAction } from './types'

const MENU_WIDTH = 220
const MENU_MAX_HEIGHT = 400
const MENU_ITEM_HEIGHT = 40
const MENU_VERTICAL_PADDING = 16
const VIEWPORT_MARGIN = 12

interface MenuPosition {
  top: number
  left: number
  direction: 'up' | 'down'
}

const TriggerContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const TriggerButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }
`

const TriggerIcon = styled.img`
  width: 16px;
  height: 16px;
  opacity: 0.7;
`

const Menu = styled.div<{ $top: number; $left: number; $direction: 'up' | 'down' }>`
  position: fixed;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  width: ${MENU_WIDTH}px;
  background-color: white;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 2147483647;
  padding: 8px 0;
  white-space: nowrap;
  max-height: ${MENU_MAX_HEIGHT}px;
  overflow-y: auto;
  overflow-x: hidden;
  animation: ${({ $direction }) => ($direction === 'up' ? 'rowActionsSlideUp' : 'rowActionsSlideDown')} 0.18s cubic-bezier(.4, 0, .2, 1);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #d0d0d0;
    border-radius: 6px;
  }

  @keyframes rowActionsSlideDown {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes rowActionsSlideUp {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`

const MenuItem = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #303030;
  transition: background-color 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};

  &:hover {
    background-color: ${({ $disabled }) => ($disabled ? 'transparent' : '#f5f5f5')};
  }

  svg {
    color: #666666;
    flex-shrink: 0;
  }
`

const computeMenuPosition = (
  buttonElement: HTMLButtonElement,
  actionsCount: number,
): MenuPosition => {
  const rowElement = buttonElement.closest('tr') as HTMLElement | null
  const buttonRect = buttonElement.getBoundingClientRect()
  const rowRect = rowElement?.getBoundingClientRect()

  const menuHeight = Math.min(
    actionsCount * MENU_ITEM_HEIGHT + MENU_VERTICAL_PADDING,
    MENU_MAX_HEIGHT,
  )

  const referenceTop = rowRect ? rowRect.top : buttonRect.top
  const referenceBottom = rowRect ? rowRect.bottom : buttonRect.bottom

  const spaceBelow = window.innerHeight - referenceBottom
  const spaceAbove = referenceTop

  let direction: 'up' | 'down' = 'down'
  let top = referenceBottom

  if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
    direction = 'up'
    top = referenceTop - menuHeight
  }

  let left = buttonRect.left + buttonRect.width / 2 - MENU_WIDTH / 2
  const maxLeft = window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN
  if (left > maxLeft) left = maxLeft
  if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN

  return { top: Math.round(top), left: Math.round(left), direction }
}

interface RowActionsMenuProps {
  actions: RowAction[]
}

const RowActionsMenu: React.FC<RowActionsMenuProps> = ({ actions }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState<MenuPosition>({ top: 0, left: 0, direction: 'down' })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (
        (buttonRef.current && buttonRef.current.contains(target)) ||
        (menuRef.current && menuRef.current.contains(target))
      ) {
        return
      }
      setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handleScrollOrResize = (event: Event) => {
      if (menuRef.current && event.target instanceof Node && menuRef.current.contains(event.target)) {
        return
      }
      setIsOpen(false)
    }
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [isOpen])

  if (actions.length === 0) return null

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (!isOpen && buttonRef.current) {
      setPosition(computeMenuPosition(buttonRef.current, actions.length))
    }
    setIsOpen((open) => !open)
  }

  const handleActionClick = (event: React.MouseEvent, action: RowAction) => {
    event.stopPropagation()
    if (action.disabled) return
    setIsOpen(false)
    action.onClick()
  }

  return (
    <>
      <TriggerContainer>
        <TriggerButton ref={buttonRef} type="button" onClick={handleToggle} aria-label="Ações">
          <TriggerIcon src={threeDotsIcon} alt="Ações" />
        </TriggerButton>
      </TriggerContainer>
      {isOpen &&
        ReactDOM.createPortal(
          <Menu ref={menuRef} $top={position.top} $left={position.left} $direction={position.direction}>
            {actions.map((action) => (
              <MenuItem
                key={action.key ?? action.label}
                $disabled={action.disabled}
                title={action.title}
                onClick={(event) => handleActionClick(event, action)}
              >
                {action.icon}
                {action.label}
              </MenuItem>
            ))}
          </Menu>,
          document.body,
        )}
    </>
  )
}

export default RowActionsMenu
